import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, EmptyState, Input, PlainLink } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { CompanyPicker, type CompanyPickerProps } from '../CompanyPicker';
import type { FlowLinks } from '../flow/FlowDetail';
import { sourceWord } from '../flow/flow-item-words';
import { IdChip } from '../ops/IdChip';
import { OpsCompanyName } from '../ops/OpsCompanyName';
import type { RecentFile, RecentFilesQuery } from './timeline-types';
import { outcomeWords, stateWord } from './timeline-words';
import './timeline.css';

export interface TimelineFinderProps {
  /** What is being asked for now. */
  query: RecentFilesQuery;
  onQuery: (query: RecentFilesQuery) => void;
  /** One company's arrivals, picked by typing its name (the server answers); absent where there is no picker. */
  company?: CompanyPickerProps | undefined;
  /** Opens the timeline an id names (a document, batch, unit or trace id). */
  onOpenId: (id: string) => void;
  /** The arrivals read so far; undefined while the first page loads. */
  files: RecentFile[] | undefined;
  /** The sentence to show when the read failed. */
  failed: string | null;
  onRetry: () => void;
  /** Reads the next page; absent on the last. */
  onMore?: (() => void) | undefined;
  loadingMore: boolean;
  searchDepth: number;
  links: FlowLinks;
}

type SearchProps = Pick<TimelineFinderProps, 'query' | 'onQuery' | 'onOpenId'>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLACEHOLDER = 'acme, AP invoices, scan_0412.pdf, or a document, batch, unit or trace id';

const narrowed = (q: RecentFilesQuery): boolean => q.text !== '' || q.from !== '' || q.to !== '';

/** What is typed, and what Find and Clear do with it: an id opens its file, anything else searches. */
function useSearchForm({ query, onQuery, onOpenId }: SearchProps) {
  const [text, setText] = useState(query.text);
  const [from, setFrom] = useState(query.from);
  const [to, setTo] = useState(query.to);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const typed = text.trim();
    if (UUID.test(typed)) onOpenId(typed);
    else onQuery({ ...query, text: typed, from, to, before: undefined });
  };
  const clear = () => {
    setText('');
    setFrom('');
    setTo('');
    onQuery({ ...query, text: '', from: '', to: '', before: undefined });
  };
  return { text, setText, from, setFrom, to, setTo, submit, clear };
}

interface DateBoxProps {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (day: string) => void;
}

/** One end of the period: a day, kept on the right side of the other end. */
function DateBox({ label, value, min, max, onChange }: DateBoxProps): ReactNode {
  return (
    <label>
      <span>{label}</span>
      <Input
        type="date"
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </label>
  );
}

/** The search: a name, a period, or an id pasted into the same box. */
function Search(props: SearchProps): ReactNode {
  const s = useSearchForm(props);
  return (
    <form className="tf-search" role="search" onSubmit={s.submit}>
      <label className="tf-search__text">
        <span>Company, job or file name, or any id</span>
        <Input
          value={s.text}
          placeholder={PLACEHOLDER}
          onChange={(e) => s.setText(e.currentTarget.value)}
        />
      </label>
      <DateBox label="From" value={s.from} max={s.to} onChange={s.setFrom} />
      <DateBox label="To" value={s.to} min={s.from} onChange={s.setTo} />
      <span className="tf-search__go">
        <Button type="submit" variant="primary">
          Find
        </Button>
        {narrowed(props.query) ? (
          <Button type="button" variant="ghost" onClick={s.clear}>
            Clear
          </Button>
        ) : null}
      </span>
    </form>
  );
}

/** "scan_0412.pdf and 2 more", or what it is when the name is not shown. */
function nameWords(f: Formatter, file: RecentFile): string {
  if (!file.fileName) return file.files > 1 ? `${f.int(file.files)} files` : 'A file';
  return file.files > 1 ? `${file.fileName} and ${f.int(file.files - 1)} more` : file.fileName;
}

function Row({ file, links, f }: { file: RecentFile; links: FlowLinks; f: Formatter }): ReactNode {
  const Link = links.Link ?? PlainLink;
  const state = stateWord(file.state);
  const company = links.company(file.company.slug);
  return (
    <li className="tf-row" data-file={file.batchId}>
      <time className="tf-row__when" dateTime={file.receivedAt} title={f.dateTime(file.receivedAt)}>
        {f.ago(file.receivedAt)}
      </time>
      <div className="tf-row__what">
        <Link href={links.timeline(file.timelineId)} className="tf-row__name">
          {nameWords(f, file)}
        </Link>
        <span className="tf-row__sub">
          {f.int(file.pages)} {file.pages === 1 ? 'page' : 'pages'} · {sourceWord(file.source)} ·{' '}
          {outcomeWords(f, file)}
        </span>
      </div>
      <div className="tf-row__whose">
        <OpsCompanyName company={file.company} href={company} Link={links.Link} />
        <span className="tf-row__sub">{file.jobName ?? 'No job'}</span>
      </div>
      <span className={`tl-state tl-state--${state.tone}`}>{state.word}</span>
      <IdChip
        id={file.batchId}
        what="batch"
        href={links.timeline(file.batchId)}
        Link={links.Link}
      />
    </li>
  );
}

function periodWords(q: RecentFilesQuery): string {
  if (q.from && q.to) return ` between ${q.from} and ${q.to}`;
  if (q.from) return ` since ${q.from}`;
  return q.to ? ` up to ${q.to}` : '';
}

/** What the list is, in a sentence: everything, or what was searched and how far the search looked. */
function scopeWords(f: Formatter, q: RecentFilesQuery, depth: number): string {
  if (!narrowed(q)) return 'Everything that arrived, newest first, across every company.';
  const period = periodWords(q);
  if (!q.text) return `Arrivals${period}, newest first.`;
  const among = `among the newest ${f.int(depth)} arrivals${period ? ' of that period' : ''}`;
  const further = q.company || period ? '' : ' Pick a company or dates to look further back.';
  return `Matching "${q.text}"${period}, ${among}.${further}`;
}

/** The arrivals, or why there are none to show. */
function Results({ files, failed, onRetry, links }: TimelineFinderProps): ReactNode {
  const f = useFormat();
  if (failed)
    return (
      <EmptyState
        compact
        title="The arrivals could not be read"
        description={failed}
        action={<Button onClick={onRetry}>Try again</Button>}
      />
    );
  if (!files) return <p className="tf-scope">Reading what arrived</p>;
  if (files.length === 0)
    return (
      <EmptyState
        compact
        title="Nothing matches"
        description="No arrival has that company, job or file name in this period. Widen the dates, or paste an id."
      />
    );
  return (
    <ul className="tf-rows" aria-label="Arrivals">
      {files.map((file) => (
        <Row key={file.batchId} file={file} links={links} f={f} />
      ))}
    </ul>
  );
}

/**
 * The Document timeline's way in, with no id needed: what arrived lately across every company, newest first, and
 * a search by company, job or file name and date. An id still works: paste it in the same box.
 */
export function TimelineFinder(props: TimelineFinderProps): ReactNode {
  const f = useFormat();
  const more = props.files && props.files.length > 0 && !props.failed ? props.onMore : undefined;
  return (
    <section className="tf" aria-label="Find a file">
      {props.company ? (
        <div className="tf-company">
          <CompanyPicker {...props.company} />
        </div>
      ) : null}
      <Search query={props.query} onQuery={props.onQuery} onOpenId={props.onOpenId} />
      <p className="tf-scope">{scopeWords(f, props.query, props.searchDepth)}</p>
      <Results {...props} />
      {more ? (
        <Button variant="secondary" onClick={more} disabled={props.loadingMore}>
          {props.loadingMore ? 'Reading' : 'Show older'}
        </Button>
      ) : null}
    </section>
  );
}
